// 网易易盾 行为式验证码（含降级方案）
// 文档：https://support.dun.163.com/documents/15588062143475712?docId=294963751458885632
// 接入说明：public/js/网易易盾行为验证码接入.md
//
// public/js/yidun-captcha.js 是易盾官方降级脚本，全局导出 initNECaptchaWithFallback：
//   1. 多域名冗余：cstaticdun.126.net → cstaticdun1.126.net 依次重试加载 load.min.js；
//   2. 服务故障兜底：连续报错达 errorFallbackCount（默认 3）次后，直接用内置兜底
//      validate 调 onVerify 放行，不把用户卡死；
//   3. 错误计数持久化在 localStorage 的 <pathname>_<captchaId>_NECAPTCHA_ERROR_COUNTS。
// 更新 public/js/yidun-captcha.js 后同步 +1，避免浏览器/CDN 拿到旧脚本
const SDK_VERSION = 1;
const SDK_SRC = `${process.env.BASE_URL || '/'}js/yidun-captcha.js?v=${SDK_VERSION}`;
// SDK 脚本加载超时时间（ms）
const LOAD_TIMEOUT = 8000;
// 实例初始化超时时间（ms）
const INIT_TIMEOUT = 10000;
// 兜底 validate 的前缀，用于判别「本次是降级放行而非用户真的滑过了」
const FALLBACK_VALIDATE_PREFIX = 'QjGAuvoHrcpuxlbw7cp4WnIbbjzG4rtSlpc7EDovNHQS.';
// 滑块弹框层级。弹框挂在 document.body 上，SDK 自带的 z-index 比页面弹窗低：
// element-ui 的 PopupManager 从 2000 起递增（.el-dialog__wrapper 与 .v-modal 遮罩同层），
// 各活动页自绘的弹框还直接写了 2001 / 9999 / 99999999 等值。不抬高的话，滑块会渲染在
// 这些遮罩底下，表现为「在弹窗里点获取验证码，滑块弹框打不开」。
// 取 32 位有符号整型上限：浏览器就是按这个上限截断 z-index 的，页面里那些 1e14 的写法
// 实际也被截到同一个值，此时同层看 DOM 顺序——滑块是后插入 body 的，仍然在上面。
const POPUP_Z_INDEX = 2147483647;
const POPUP_STYLE_ID = 'yidun-captcha-popup-style';

// 公共配置 key：1 打开滑块验证，0 跳过滑块直接发码
export const TOGGLE_CONFIG_KEY = 'BehavioralVerificationToggle';
// 公共配置 key：易盾行为验证码业务ID（captchaId）
export const CAPTCHA_ID_CONFIG_KEY = 'BehavioralVerificationID';

let sdkPromise = null;

function appendScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    let timer = null;
    const done = (error) => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      script.onload = null;
      script.onerror = null;
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };
    script.type = 'text/javascript';
    script.async = true;
    script.charset = 'utf-8';
    script.src = src;
    script.onload = () => done();
    script.onerror = () => done(new Error(`Failed to load script(${src}).`));
    timer = setTimeout(() => done(new Error(`Load script timeout(${src}).`)), LOAD_TIMEOUT);
    (document.head || document.getElementsByTagName('head')[0]).appendChild(script);
  });
}

// 把滑块弹框钉在视口正中、并抬到页面弹窗之上。
// SDK 样式表后注入、且弹框的 top/left 是 SDK 按打开时的滚动位置算出来写在行内的，
// 所以这里统一用 !important：行内样式压不过样式表里的 !important，注入顺序也就不重要了。
function ensurePopupStyle() {
  if (document.getElementById(POPUP_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = POPUP_STYLE_ID;
  // .yidun_popup 是 popup 模式下挂在 body 上的弹框本体（§八 里让你 querySelector 的就是它）。
  // 带上 body 前缀把优先级提到 (0,1,1)，即使 SDK 自己那条也写了 !important 也能压过它。
  // 只改 z-index 不够：弹框是按文档定位的，页面滚动超过一屏、又被 element-ui 的
  // el-popup-parent--hidden 锁死滚动时，它会停在文档顶部——弹窗还开着就没法滑上去看。
  // 所以 position 换成 fixed，并把 SDK 算出来的 top/left/margin 一并清掉，改为视口居中。
  style.textContent =
    'body .yidun_popup{' +
    'position:fixed !important;' +
    'top:50% !important;' +
    'left:50% !important;' +
    'right:auto !important;' +
    'bottom:auto !important;' +
    'margin:0 !important;' +
    'transform:translate(-50%,-50%) !important;' +
    `z-index:${POPUP_Z_INDEX} !important;` +
    '}';
  (document.head || document.getElementsByTagName('head')[0]).appendChild(style);
}

// 懒加载 SDK，全站只注入一次；加载失败置回 null 允许下次重试
export function loadYidunSdk() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('易盾验证码只能在浏览器环境使用'));
  }
  ensurePopupStyle();
  if (window.initNECaptchaWithFallback) {
    return Promise.resolve(window.initNECaptchaWithFallback);
  }
  if (!sdkPromise) {
    sdkPromise = appendScript(SDK_SRC)
      .then(() => {
        if (!window.initNECaptchaWithFallback) {
          throw new Error('易盾验证码 SDK 加载异常');
        }
        return window.initNECaptchaWithFallback;
      })
      .catch((error) => {
        sdkPromise = null;
        throw error;
      });
  }
  return sdkPromise;
}

let captchaIns = null;
// 实例序号：destroyInstance 时 +1，让已销毁实例的 onVerify / onClose 自行失效，防串台
let insToken = 0;
// 当前实例是否已经产出过 validate。validate 是一次性的，用过必须换新题
let usedUp = false;
// 正在准备弹框（读配置 + 加载 SDK + 初始化实例），期间重复点击直接忽略
let opening = false;
// 当前等待接收验证结果的回调
let onVerified = null;
let onClosed = null;

function clearCallbacks() {
  onVerified = null;
  onClosed = null;
}

// 销毁实例。validate 一次性，验证通过后必须销毁重建换新题：
// 曾用 instance.refresh()，但 refresh 换题是异步的，紧接着 verify() 会让用户滑在旧题上，
// 校验必然失败 —— 表现为「第二次验证完不请求接口，第三次才请求」。
function destroyInstance() {
  insToken += 1;
  const instance = captchaIns;
  captchaIns = null;
  usedUp = false;
  if (instance) {
    try {
      instance.destroy();
    } catch (e) {
      // 忽略销毁异常
    }
  }
}

function initInstance(captchaId, { mode, width }) {
  insToken += 1;
  const token = insToken;
  return loadYidunSdk().then(
    (initNECaptchaWithFallback) =>
      new Promise((resolve, reject) => {
        let settled = false;
        const timer = setTimeout(() => {
          if (!settled) {
            settled = true;
            reject(new Error('易盾验证码初始化超时'));
          }
        }, INIT_TIMEOUT);
        const settle = (fn, value) => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          fn(value);
        };
        initNECaptchaWithFallback(
          {
            // popup 模式官方要求 element 传 document.body；
            // 传自建容器弹框会留在文档流里，渲染到页面最底部
            element: document.body,
            captchaId,
            mode,
            width,
            lang: 'zh-CN',
            apiVersion: 2,
            // 强制滑动拼图。不传的话若控制台把该 captchaId 配成「智能无感」，
            // SDK 会不显示任何交互直接判过，表现为「滑块没弹出来就走到下一步」
            captchaType: 'jigsaw',
            // 不要传 closeEnable: true —— 它的含义是「把关闭时机交给业务侧」，
            // 会隐藏右上角关闭按钮且验证成功后不再自动收起弹框。保持默认 false，
            // 由 SDK 自己提供关闭按钮并在验证通过后自动关闭。
            onVerify: (err, data) => {
              // 已销毁实例的回调，直接丢弃
              if (token !== insToken) {
                return;
              }
              // 验证失败：SDK 弹框自己会提示并换题，保留回调等用户重试，
              // 不能在这里把回调清掉，否则重试成功也没人接收结果
              if (err) {
                return;
              }
              const validate = (data && data.validate) || '';
              if (validate.indexOf(FALLBACK_VALIDATE_PREFIX) === 0) {
                console.warn('[yidun captcha] 已触发降级，使用兜底 validate');
              }
              // validate 一次性，标记待换题
              usedUp = true;
              const callback = onVerified;
              clearCallbacks();
              callback && callback(validate, data);
            },
            onClose: () => {
              if (token !== insToken) {
                return;
              }
              // 验证通过后的自动关闭，回调已在 onVerify 里消费掉，不用再处理
              if (usedUp) {
                return;
              }
              // 用户主动关闭弹框，放弃本次获取验证码
              const callback = onClosed;
              clearCallbacks();
              callback && callback();
            },
          },
          (instance) => {
            captchaIns = instance;
            settle(resolve, instance);
          },
          (error) => settle(reject, error)
        );
      })
  );
}

/**
 * 弹出行为验证码。
 *
 * 用户滑动通过后回调 onVerify(validate)；用户关闭弹框回调 onClose（页面无需处理取消分支）。
 * SDK 加载或实例初始化失败时 reject，由调用方提示。
 */
export async function openYidunCaptcha({
  captchaId,
  onVerify,
  onClose,
  mode = 'popup',
  width = '320px',
}) {
  // 初始化期间重复点击直接忽略
  if (opening) {
    return;
  }
  // 入口先清一次，避免上一次遗留的回调被本次的结果触发
  clearCallbacks();
  opening = true;
  try {
    if (captchaIns && usedUp) {
      destroyInstance();
    }
    if (!captchaIns) {
      await initInstance(captchaId, { mode, width });
    }
    // 实例就绪后再挂回调，避免销毁旧实例时被它的 onClose 清掉
    onVerified = onVerify;
    onClosed = onClose;
    captchaIns.verify();
  } finally {
    opening = false;
  }
}

// 摘掉回调并销毁实例，用于组件卸载时清场
export function destroyYidunCaptcha() {
  clearCallbacks();
  destroyInstance();
}
